import type { LocaleIso } from 'utils';
import type { PaymentActionFailure, PaymentMethod } from './types';

/**
 * PSI payment status codes.
 *
 * Ported from Gatsby: packages/core/src/modules/Cart/constants.ts
 */
export const PAYMENT_STATUS_CODES = {
  WAITING: 1,
  WAITING_VARIANT: 12,
  SUCCESS: 2
} as const;

export const PAYMENT_METHOD = {
  INVOICE: 'invoice',
  PAYPAL: 'paypal',
  CREDIT_CARD: 'gui_cc',
  SEPA: 'sepa',
  DANKORT: 'gui_dankort',
  MAESTRO: 'gui_dc',
  IDEAL: 'ideal',
  MOBILE_PAY: 'mobile_pay',
  TWINT: 'twint'
} as const;

/** Not offered as an option: a previous PayPal Express payment preselects PayPal (Gatsby findPaymentByMethod). */
export const PAYPAL_EXPRESS_METHOD = 'gui_pp_express';

/** `gui_dc` is the only non-card method whose PSI `payment_type` differs from its key. */
export const MAESTRO_PAYMENT_TYPE = 'gui_maestro';

export const PAYMENT_CARD_TYPE = {
  VISA: 'gui_visa',
  MASTER: 'gui_master',
  AMEX: 'gui_amex'
} as const;

/** Mirrors Gatsby getPopularPaymentMethodByCountry (ids 1 DE, 2 NL, 3 AT, 4 CH); every other locale falls back below. */
export const POPULAR_PAYMENT_METHOD_BY_LOCALE: Partial<Record<LocaleIso, PaymentMethod>> = {
  'de-DE': PAYMENT_METHOD.PAYPAL,
  'nl-NL': PAYMENT_METHOD.IDEAL,
  'de-AT': PAYMENT_METHOD.INVOICE,
  'de-CH': PAYMENT_METHOD.INVOICE,
  'fr-CH': PAYMENT_METHOD.INVOICE,
  'it-CH': PAYMENT_METHOD.INVOICE
};

export const DEFAULT_POPULAR_PAYMENT_METHOD: PaymentMethod = PAYMENT_METHOD.CREDIT_CARD;

/** Gatsby isAmexDisabled: country ids 4 CH, 10 DK, 11 SE, 15 IE. */
export const AMEX_DISABLED_LOCALES: readonly LocaleIso[] = ['de-CH', 'fr-CH', 'it-CH', 'da-DK', 'sv-SE', 'en-IE'];

export const PAYMENT_LABEL_KEYS = {
  [PAYMENT_METHOD.INVOICE]: 'i18n_payment_invoice',
  [PAYMENT_METHOD.PAYPAL]: 'i18n_payment_paypal',
  [PAYMENT_METHOD.CREDIT_CARD]: 'i18n_payment_credit_card',
  [PAYMENT_METHOD.SEPA]: 'i18n_payment_sepa',
  [PAYMENT_METHOD.DANKORT]: 'i18n_payment_dankort',
  [PAYMENT_METHOD.MAESTRO]: 'i18n_payment_maestro',
  [PAYMENT_METHOD.IDEAL]: 'i18n_payment_iDeal',
  [PAYMENT_METHOD.MOBILE_PAY]: 'i18n_payment_mobile_pay',
  [PAYMENT_METHOD.TWINT]: 'i18n_payment_twint'
} as const satisfies Record<PaymentMethod, string>;

/** Gatsby's Contentful section ids of the method descriptions. Lastschrift has none: it shows the SEPA form instead. */
export const PAYMENT_DESCRIPTION_SECTION_ID: Partial<Record<PaymentMethod, string>> = {
  [PAYMENT_METHOD.CREDIT_CARD]: 'section_cart_payment_credit_card_desc',
  [PAYMENT_METHOD.PAYPAL]: 'section_cart_payment_paypal_desc',
  [PAYMENT_METHOD.INVOICE]: 'section_cart_payment_invoice_desc',
  [PAYMENT_METHOD.IDEAL]: 'section_cart_payment_ideal_desc',
  [PAYMENT_METHOD.TWINT]: 'section_cart_payment_twint_desc',
  [PAYMENT_METHOD.MOBILE_PAY]: 'section_cart_payment_mobile_desc',
  [PAYMENT_METHOD.DANKORT]: 'section_cart_payment_dankort_desc',
  [PAYMENT_METHOD.MAESTRO]: 'section_cart_payment_maestro_desc'
};

/** `@pixum/icon` CDN names per method. */
export const PAYMENT_METHOD_ICON_NAME: Partial<Record<PaymentMethod, string>> = {
  [PAYMENT_METHOD.INVOICE]: 'invoice',
  [PAYMENT_METHOD.PAYPAL]: 'paypal',
  [PAYMENT_METHOD.SEPA]: 'sepa',
  [PAYMENT_METHOD.IDEAL]: 'ideal',
  [PAYMENT_METHOD.DANKORT]: 'dankort',
  [PAYMENT_METHOD.MAESTRO]: 'maestro',
  [PAYMENT_METHOD.MOBILE_PAY]: 'mobile-pay',
  [PAYMENT_METHOD.TWINT]: 'twint'
};

/** `@pixum/icon` CDN names per card type. */
export const PAYMENT_CARD_ICON_NAME = {
  [PAYMENT_CARD_TYPE.VISA]: 'visa',
  [PAYMENT_CARD_TYPE.MASTER]: 'mastercard',
  [PAYMENT_CARD_TYPE.AMEX]: 'american-express'
} as const;

/** Gatsby mapPaymentTypesToLabel keys for the card-type combobox options. */
export const PAYMENT_CARD_LABEL_KEYS = {
  [PAYMENT_CARD_TYPE.VISA]: 'i18n_payment_visa',
  [PAYMENT_CARD_TYPE.MASTER]: 'i18n_payment_mastercard',
  [PAYMENT_CARD_TYPE.AMEX]: 'i18n_payment_american_express'
} as const;

export const SEPA_FIELD = {
  HOLDER: 'holder',
  IBAN: 'iban',
  BIC: 'bic',
  BANK_NAME: 'bankName',
  ACCEPTED: 'accepted'
} as const;

/** DOM input name per field, used to focus the first invalid input. */
export const SEPA_INPUT_NAME = {
  [SEPA_FIELD.HOLDER]: 'holder',
  [SEPA_FIELD.IBAN]: 'iban',
  [SEPA_FIELD.BIC]: 'bic',
  [SEPA_FIELD.BANK_NAME]: 'bankName',
  [SEPA_FIELD.ACCEPTED]: 'sepaAcceptedCheckbox'
} as const;

/** Names of the payment form's hidden inputs, read by setPaymentAction and useSetPayment. */
export const PAYMENT_FORM_FIELD = {
  METHOD: 'method',
  TYPE: 'type',
  SEPA_ACCEPTED: 'sepaAccepted',
  TRACKING_METHOD: 'trackingMethod',
  IS_PAYMENT_NEEDED: 'isPaymentNeeded'
} as const;

/** A failed set-payment with nothing to show, as in Gatsby: it is only logged and tracked. */
export const SET_PAYMENT_ACTION_FAILURE: PaymentActionFailure = { success: false, fieldErrors: {} };

export const PAYMENT_FORM_ID = 'cart-payment-form';
export const PAYMENT_TITLE_ID = 'cart-payment-title';
